import {
  WSSelect,
  WSSelectOption,
  WSSelectPropsSingle
} from "@wingspanhq/fe-component-library";
import { PayeeEngagementStatus } from "@wingspanhq/wingspan-api-sdk";
import { useEffect, useMemo } from "react";
import { queriesPayeeEngagements } from "@query";
import { useXWingspanPayerId } from "../../routes/RoutePayeeDetails/useXWingspanPayerId";

type Props = Omit<
  WSSelectPropsSingle,
  "options" | "value" | "onChange" | "mode"
> & {
  payeeId?: string | null;
  value: string | null;
  onChange: (value: string | null) => void;
  /**
   * Select the only option as soon as it resolves. Most contractors have a
   * single (default) assignment, so this keeps the two-picker flow a one-click
   * flow for them.
   */
  autoSelectSingleOption?: boolean;
};

/**
 * An assignment is payable from the moment it exists: v3 creates a
 * payee-engagement `Created` and moves it to `Activated` once the contractor
 * onboards, but v1 let a payer create a payable for a contractor who had not yet
 * signed up — the payable simply waited. Matches the Active grouping on the
 * contractor-details screen, which also treats `Created` as live.
 */
const SELECTABLE_STATUSES: PayeeEngagementStatus[] = [
  PayeeEngagementStatus.CREATED,
  PayeeEngagementStatus.ACTIVATED
];

/**
 * Assignment (payee-engagement) picker for an already-selected contractor.
 *
 * Reads the regular `listPayeeEngagements` collection rather than the payee
 * search rows: the engagements belong to one known payee, so there is nothing to
 * search across and the list endpoint is authoritative the moment an engagement
 * is created (the search projection lags).
 */
export const SelectEngagementForPayee: React.FC<Props> = props => {
  const { payeeId, value, onChange, autoSelectSingleOption, ...selectProps } =
    props;

  const xWingspanAccount = useXWingspanPayerId();

  const queryEngagements =
    queriesPayeeEngagements.listPayeeEngagements.useQueryAll(
      { payeeId: payeeId as string, xWingspanAccount },
      { enabled: !!payeeId }
    );

  const options = useMemo<WSSelectOption[]>(
    () =>
      (queryEngagements.flatData ?? [])
        .filter(
          engagement =>
            // The selected engagement stays listed whatever its status, so a
            // payable on a since-archived assignment still renders its name on
            // the disabled (edit-mode) picker.
            engagement.id === value ||
            (SELECTABLE_STATUSES.includes(engagement.status!) &&
              // The v1 `labels.duplicate` flag lives on the v3 `metadata` map,
              // whose values are strings — the boolean `true` becomes `"true"`.
              engagement.metadata?.duplicate !== "true")
        )
        .map(engagement => ({
          value: engagement.id as string,
          label: engagement.engagementName || "Assignment"
        })),
    [queryEngagements.flatData, value]
  );

  // Resolves back to undefined once the value lands, so this settles in one pass.
  const singleOptionValue =
    autoSelectSingleOption &&
    !value &&
    queryEngagements.isSuccess &&
    options.length === 1
      ? options[0].value
      : undefined;

  useEffect(() => {
    if (singleOptionValue) {
      onChange(singleOptionValue);
    }
  }, [singleOptionValue, onChange]);

  return (
    <WSSelect
      {...selectProps}
      mode="single"
      value={value}
      onChange={newValue => onChange(newValue ?? null)}
      placeholder={selectProps.placeholder ?? "Select an option"}
      options={options}
      internalSearch={selectProps.internalSearch ?? true}
      // Splitting the pickers makes a contractor with no selectable assignment
      // reachable, where the combined picker simply omitted them — say so instead
      // of dropping the user into an empty menu.
      emptyState={
        selectProps.emptyState ?? {
          type: "documents",
          title: "No assignments available",
          description:
            "This contractor has no active assignment to pay against yet."
        }
      }
      status={
        queryEngagements.isInitialLoading ? "loading" : selectProps.status
      }
      isListLoading={queryEngagements.isLoading}
    />
  );
};
